import type { PaymentReportInfo } from '@/api/payment-prep';
import { FOLDER_GROUPS, folderGroupOf } from '@/payment/payment-steps';
import { FOLDER_GROUP_CHIPS, ROUND_CARD, SMALL_PILL } from '@/payment/payment-styles';

/**
 * PaymentReportLists の props。
 */
export interface PaymentReportListsProps {
  /** レポートとフォルダの一覧 */
  info: PaymentReportInfo;
  /** Old へ移す（名前と Drive の ID） */
  onMoveToOld: (name: string, id: string) => void;
}

/** @graph-connects none */
const LINK =
  'min-w-0 flex-1 truncate text-sm text-[var(--color-text-primary)] no-underline hover:text-[var(--color-accent)] hover:underline';

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 支払管理レポート（新しい順・先頭に最新の札）と今月の証憑整理フォルダ（15日・りそな・みずほの色分け）を 2 枚の丸いカードで並べる。どれも Old へ移せる
 * @graph-connects folderGroupOf [calls] フォルダを口座ごとに仕分ける
 */
export function PaymentReportLists({ info, onMoveToOld }: PaymentReportListsProps) {
  const groups = FOLDER_GROUPS.map((group) => ({
    group,
    items: info.folders.filter((folder) => folderGroupOf(folder.name) === group),
  })).filter((entry) => entry.items.length > 0);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className={`${ROUND_CARD} px-5 py-4`} aria-label="支払管理レポート">
        <h2 className="m-0 mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--color-text-primary)]">
          支払管理レポート
          <span className="rounded-full bg-[var(--color-bg-subtle)] px-2 text-xs font-normal text-[var(--color-text-secondary)]">
            {info.reports.length} 件
          </span>
        </h2>
        {info.reports.length === 0 ? (
          <p className="m-0 text-sm text-[var(--color-text-muted)]">
            まだありません。STEP 1 で作成してください
          </p>
        ) : (
          <ul className="m-0 list-none space-y-1.5 p-0">
            {info.reports.map((file, index) => (
              <li
                key={file.id}
                className={
                  index === 0
                    ? 'flex items-center gap-2 rounded-xl bg-[var(--color-doing-bg)] px-3 py-2'
                    : 'flex items-center gap-2 rounded-xl px-3 py-1.5 hover:bg-[var(--color-bg-subtle)]'
                }
              >
                {index === 0 ? (
                  <span className="shrink-0 rounded-full bg-[var(--color-doing-border)] px-2 py-0.5 text-xs font-medium text-[var(--color-bg-surface)]">
                    最新
                  </span>
                ) : null}
                <a href={file.url} target="_blank" rel="noreferrer" className={LINK}>
                  {file.name}
                </a>
                <span className="shrink-0 text-xs tabular-nums text-[var(--color-text-muted)]">
                  {file.updated}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    onMoveToOld(file.name, file.id);
                  }}
                  className={SMALL_PILL}
                >
                  Old へ移動
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={`${ROUND_CARD} px-5 py-4`} aria-label="今月の証憑整理フォルダ">
        <h2 className="m-0 mb-3 text-sm font-semibold text-[var(--color-text-primary)]">
          今月の証憑整理フォルダ
        </h2>
        {groups.length === 0 ? (
          <p className="m-0 text-sm text-[var(--color-text-muted)]">まだありません</p>
        ) : (
          <ul className="m-0 list-none space-y-1.5 p-0">
            {groups.flatMap(({ group, items }) =>
              items.map((folder) => (
                <li
                  key={folder.id}
                  className="flex items-center gap-2 rounded-xl px-3 py-1.5 hover:bg-[var(--color-bg-subtle)]"
                >
                  <span
                    className={`w-14 shrink-0 rounded-full py-0.5 text-center text-xs font-medium ${FOLDER_GROUP_CHIPS[group]}`}
                  >
                    {group}
                  </span>
                  <a href={folder.url} target="_blank" rel="noreferrer" className={LINK}>
                    {folder.name}
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      onMoveToOld(folder.name, folder.id);
                    }}
                    className={SMALL_PILL}
                  >
                    Old へ移動
                  </button>
                </li>
              )),
            )}
          </ul>
        )}
      </section>
    </div>
  );
}
